/**
 * Scans list page
 * Table of all discovery scans with status, progress, and filters
 */

import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, RefreshCw, Trash2 } from 'lucide-react'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Skeleton } from '@/components/ui/skeleton'
import { ScanStatusBadge, ScanProgress } from '@/components/discovery'
import { useScans, useConnections, useStartScan, useDeleteScan } from '@/hooks/use-discovery'
import { formatTimestamp, formatDuration } from '@/lib/discovery-utils'
import type { ScanQueryParams, ScanStatus } from '@/types/discovery'

export function DiscoveryScansPage() {
  const navigate = useNavigate()

  const [filters, setFilters] = useState<ScanQueryParams>({
    page: 1,
    pageSize: 20,
  })

  // Start scan dialog state
  const [startDialogOpen, setStartDialogOpen] = useState(false)
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('')
  const [enableAi, setEnableAi] = useState(true)

  // Delete scan dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [scanToDelete, setScanToDelete] = useState<string | null>(null)

  const { data, isLoading, refetch } = useScans(filters)
  const { data: connectionsData } = useConnections({ pageSize: 100 })
  const startScanMutation = useStartScan()
  const deleteScanMutation = useDeleteScan()

  const scans = data?.data ?? []
  const pagination = data?.pagination
  const connections = connectionsData?.data ?? []

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters({
        ...filters,
        page: 1,
        status: value === 'all' ? undefined : (value as ScanStatus),
      })
    },
    [filters]
  )

  const handleConnectionFilter = useCallback(
    (value: string) => {
      setFilters({
        ...filters,
        page: 1,
        connectionId: value === 'all' ? undefined : value,
      })
    },
    [filters]
  )

  const handlePageChange = useCallback(
    (newPage: number) => {
      setFilters({ ...filters, page: newPage })
    },
    [filters]
  )

  const handleStartScan = useCallback(() => {
    if (!selectedConnectionId) return
    startScanMutation.mutate(
      {
        connectionId: selectedConnectionId,
        enableAiClassification: enableAi,
      },
      {
        onSuccess: () => {
          setStartDialogOpen(false)
          setSelectedConnectionId('')
        },
      }
    )
  }, [selectedConnectionId, enableAi, startScanMutation])

  const handleDeleteRequest = useCallback((e: React.MouseEvent, scanId: string) => {
    e.stopPropagation()
    setScanToDelete(scanId)
    setDeleteDialogOpen(true)
  }, [])

  const handleDeleteConfirm = useCallback(() => {
    if (!scanToDelete) return
    deleteScanMutation.mutate(scanToDelete, {
      onSuccess: () => {
        setDeleteDialogOpen(false)
        setScanToDelete(null)
      },
    })
  }, [scanToDelete, deleteScanMutation])

  return (
    <>
      {/* Header */}
      <header className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <h1 className="text-base font-medium">Discovery Scans</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={isLoading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'}
            />
            <span className="sr-only">Refresh</span>
          </Button>

          <Dialog open={startDialogOpen} onOpenChange={setStartDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="h-8">
                <Plus className="mr-2 h-3.5 w-3.5" />
                Start Scan
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Start Discovery Scan</DialogTitle>
                <DialogDescription>
                  Select a connection and configure the scan.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Connection</Label>
                  <Select
                    value={selectedConnectionId}
                    onValueChange={setSelectedConnectionId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a connection" />
                    </SelectTrigger>
                    <SelectContent>
                      {connections.map((conn) => (
                        <SelectItem key={conn.id} value={conn.id}>
                          {conn.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="enableAi"
                    checked={enableAi}
                    onCheckedChange={(checked) => setEnableAi(checked === true)}
                  />
                  <Label htmlFor="enableAi" className="text-sm font-normal">
                    Enable AI-assisted classification
                  </Label>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setStartDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleStartScan}
                  disabled={!selectedConnectionId || startScanMutation.isPending}
                >
                  Start Scan
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Scan</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this scan? This will permanently remove the scan
              and all its classification results. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setScanToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-4 p-4 md:gap-6 md:p-6">
        <div>
          <p className="text-sm text-muted-foreground">
            View and manage data discovery scans across all connections.
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <Select
            value={filters.connectionId ?? 'all'}
            onValueChange={handleConnectionFilter}
          >
            <SelectTrigger className="h-8 w-[200px]">
              <SelectValue placeholder="Connection" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Connections</SelectItem>
              {connections.map((conn) => (
                <SelectItem key={conn.id} value={conn.id}>
                  {conn.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.status ?? 'all'}
            onValueChange={handleStatusFilter}
          >
            <SelectTrigger className="h-8 w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="PENDING">Queued</SelectItem>
              <SelectItem value="RUNNING">Running</SelectItem>
              <SelectItem value="COMPLETED">Complete</SelectItem>
              <SelectItem value="FAILED">Failed</SelectItem>
              <SelectItem value="CANCELLED">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}

        {/* Empty */}
        {!isLoading && scans.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-muted-foreground">
              {filters.status || filters.connectionId
                ? 'No scans found matching your filters.'
                : 'No scans yet. Start a scan to classify data.'}
            </p>
          </div>
        )}

        {/* Table */}
        {!isLoading && scans.length > 0 && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Connection</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead className="text-right">Columns</TableHead>
                  <TableHead className="text-right">Flagged</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {scans.map((scan) => {
                  const conn = connections.find(
                    (c) => c.id === scan.connectionId
                  )
                  return (
                    <TableRow
                      key={scan.id}
                      className="cursor-pointer"
                      onClick={() =>
                        navigate(`/discovery/scans/${scan.id}/results`)
                      }
                    >
                      <TableCell className="font-medium">
                        {conn?.name ?? scan.connectionId.slice(0, 8)}
                      </TableCell>
                      <TableCell>
                        <ScanStatusBadge status={scan.status} />
                      </TableCell>
                      <TableCell className="w-32">
                        <ScanProgress scan={scan} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {scan.columnsClassified}/{scan.columnsScanned}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {0}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatTimestamp(scan.startedAt)}
                      </TableCell>
                      <TableCell className="text-muted-foreground tabular-nums">
                        {formatDuration(scan.startedAt, scan.completedAt)}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={(e) => handleDeleteRequest(e, scan.id)}
                          disabled={deleteScanMutation.isPending && scanToDelete === scan.id}
                          title="Delete scan"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span className="sr-only">Delete scan</span>
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="flex items-center justify-between pt-4">
                <p className="text-sm text-muted-foreground">
                  Showing {scans.length} of {pagination.totalItems} scan
                  {pagination.totalItems === 1 ? '' : 's'}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPreviousPage}
                  >
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}
